/**
 * MakeoverApproachSection — the "how it works" explainer:
 *   AI handles the heavy lifting · expert-curated guidance decides
 *   what moves to the front of the profile.
 *
 * Three supporting points below: Clarity, Relevance, Consistency.
 */

import React from 'react';
import { motion } from 'framer-motion';
import { Cpu, UserCheck, Lightbulb, Target, Layers } from 'lucide-react';
import {
  fadeUp,
  kicker,
  displaySection,
  sectionWrap,
  sectionInner,
} from './shared';

const POINTS = [
  {
    icon: Lightbulb,
    title: 'Clarity',
    body: 'Make your value easier to recognise.',
    testid: 'approach-clarity',
  },
  {
    icon: Target,
    title: 'Relevance',
    body: 'Bring forward the experience that matters.',
    testid: 'approach-relevance',
  },
  {
    icon: Layers,
    title: 'Consistency',
    body: 'Connect your résumé and LinkedIn story.',
    testid: 'approach-consistency',
  },
];

export const MakeoverApproachSection = () => (
  <section
    className={sectionWrap}
    aria-label="Our approach — AI plus expert-curated thinking"
    data-testid="makeover-approach-section"
  >
    <div className={sectionInner}>
      <motion.div {...fadeUp} className="max-w-3xl">
        <p className={kicker}>Our approach</p>
        <h2
          className={`${displaySection} mt-4 text-3xl sm:text-4xl lg:text-5xl`}
          data-testid="approach-headline"
        >
          Powered by AI.{' '}
          <span className="text-primary">Guided by expert-curated thinking.</span>
        </h2>
        <p className="mt-5 text-base sm:text-lg text-muted-foreground leading-relaxed">
          AI helps analyse and rewrite your content. Expert-curated guidance
          shapes how your strengths, experience, and career story are
          presented.
        </p>
        <p className="mt-3 text-base sm:text-lg text-muted-foreground leading-relaxed">
          The result: clearer language, more relevant positioning, and a
          professional profile that is easier to understand.
        </p>
      </motion.div>

      {/* Dual-icon ribbon — AI + expert */}
      <motion.div
        {...fadeUp}
        className="mt-10 inline-flex items-center gap-6 rounded-full border border-border/70 bg-card/80 backdrop-blur px-5 py-3"
      >
        <span className="inline-flex items-center gap-2 text-[12.5px] text-foreground">
          <Cpu size={14} className="text-primary" aria-hidden="true" />
          AI analysis
        </span>
        <span
          className="h-4 w-px bg-border"
          aria-hidden="true"
        />
        <span className="inline-flex items-center gap-2 text-[12.5px] text-foreground">
          <UserCheck size={14} className="text-primary" aria-hidden="true" />
          Expert-curated guidance
        </span>
      </motion.div>

      {/* Three supporting points */}
      <div className="mt-12 grid gap-5 md:grid-cols-3">
        {POINTS.map((p, i) => (
          <motion.div
            key={p.testid}
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.05 + i * 0.05 }}
            className="rounded-3xl border border-border/70 bg-card/70 p-7"
            data-testid={p.testid}
          >
            <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 text-primary">
              <p.icon size={18} strokeWidth={2.2} aria-hidden="true" />
            </span>
            <h3 className="mt-5 text-lg font-semibold tracking-[-0.01em]">
              {p.title}
            </h3>
            <p className="mt-2 text-[13.5px] text-muted-foreground leading-relaxed">
              {p.body}
            </p>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

export default MakeoverApproachSection;
